import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Admin.css';
import API_BASE from '../config';

function AccessControl() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [accessControls, setAccessControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewAccess, setShowNewAccess] = useState(false);
  const [formData, setFormData] = useState({
    userId: '',
    assetId: '',
    accessLevel: 'READ',
    accessType: 'PERMANENT',
    validFrom: new Date().toISOString().slice(0, 16),
    validUntil: '',
    reason: ''
  });

  const ACCESS_LEVELS = ['READ', 'WRITE', 'EXECUTE', 'ADMIN'];
  const ACCESS_TYPES = ['PERMANENT', 'TEMPORARY', 'CONDITIONAL'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch users
      const usersRes = await fetch(`${API_BASE}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch assets
      const assetsRes = await fetch(`${API_BASE}/admin/assets`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Fetch access controls
      const accessRes = await fetch(`${API_BASE}/admin/access-control`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (usersRes.ok && assetsRes.ok && accessRes.ok) {
        const usersData = await usersRes.json();
        const assetsData = await assetsRes.json();
        const accessData = await accessRes.json();
        
        setUsers(usersData.users || []);
        setAssets(assetsData.assets || []);
        setAccessControls(accessData.accessControls || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGrantAccess = async (e) => {
    e.preventDefault();
    
    if (!formData.userId || !formData.assetId) {
      alert('Please select user and asset');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/admin/access-control`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: parseInt(formData.userId),
          assetId: parseInt(formData.assetId),
          accessLevel: formData.accessLevel,
          accessType: formData.accessType,
          validFrom: new Date(formData.validFrom).toISOString(),
          validUntil: formData.validUntil ? new Date(formData.validUntil).toISOString() : null,
          reason: formData.reason
        })
      });

      if (response.ok) {
        alert('Access granted successfully');
        setShowNewAccess(false);
        setFormData({
          userId: '',
          assetId: '',
          accessLevel: 'READ',
          accessType: 'PERMANENT',
          validFrom: new Date().toISOString().slice(0, 16),
          validUntil: '',
          reason: ''
        });
        fetchData();
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to grant access');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleRevokeAccess = async (accessId) => {
    const reason = prompt('Enter revocation reason:');
    if (!reason) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/admin/access-control/${accessId}/revoke`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ revocationReason: reason })
      });

      if (response.ok) {
        alert('Access revoked successfully');
        fetchData();
      } else {
        alert('Failed to revoke access');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  if (loading) return <div className="admin-container"><p>Loading...</p></div>;

  return (
    <div className="admin-container">
      <div className="page-header">
        <button onClick={() => navigate('/admin')} className="btn-back-nav" style={{ marginBottom: 12 }}>← Back</button>
        <h1>Access Control Management</h1>
        <p>Principle of Least Access (PoLA) - Grant and manage user access to assets</p>
        <button 
          onClick={() => setShowNewAccess(!showNewAccess)}
          className="btn-primary"
        >
          {showNewAccess ? 'Cancel' : '+ Grant New Access'}
        </button>
      </div>

      {/* New Access Form */}
      {showNewAccess && (
        <form onSubmit={handleGrantAccess} className="form-section">
          <h2>Grant New Access</h2>
          
          <div className="form-group">
            <label>User</label>
            <select 
              value={formData.userId}
              onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
              required
            >
              <option value="">Select User</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} ({user.email})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Asset</label>
            <select 
              value={formData.assetId}
              onChange={(e) => setFormData({ ...formData, assetId: e.target.value })}
              required
            >
              <option value="">Select Asset</option>
              {assets.map(asset => (
                <option key={asset.id} value={asset.id}>
                  {asset.name} ({asset.assetType})
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Access Level</label>
              <select 
                value={formData.accessLevel}
                onChange={(e) => setFormData({ ...formData, accessLevel: e.target.value })}
              >
                {ACCESS_LEVELS.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Access Type</label>
              <select 
                value={formData.accessType}
                onChange={(e) => setFormData({ ...formData, accessType: e.target.value })}
              >
                {ACCESS_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Valid From</label>
              <input 
                type="datetime-local"
                value={formData.validFrom}
                onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Valid Until (Leave empty for permanent)</label>
              <input 
                type="datetime-local"
                value={formData.validUntil}
                onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Reason for Access</label>
            <textarea 
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="Document the business reason for this access"
              rows="3"
            />
          </div>

          <button type="submit" className="btn-primary">Grant Access</button>
        </form>
      )}

      {/* Active Access Controls */}
      <div className="section">
        <h2>Active Access Controls</h2>
        <p>Manage user access with principle of least access</p>
        
        <div className="table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Asset</th>
                <th>Access Level</th>
                <th>Type</th>
                <th>Valid From</th>
                <th>Valid Until</th>
                <th>Approved By</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {accessControls.filter(ac => ac.isActive).map(access => (
                <tr key={access.id}>
                  <td>{access.user?.firstName} {access.user?.lastName}</td>
                  <td>{access.asset?.name}</td>
                  <td><span className="badge">{access.accessLevel}</span></td>
                  <td>{access.accessType}</td>
                  <td>{new Date(access.validFrom).toLocaleDateString()}</td>
                  <td>{access.validUntil ? new Date(access.validUntil).toLocaleDateString() : 'Permanent'}</td>
                  <td>{access.approver?.firstName} {access.approver?.lastName}</td>
                  <td>
                    <span className={`status-badge ${access.isRevoked ? 'revoked' : 'active'}`}>
                      {access.isRevoked ? 'Revoked' : 'Active'}
                    </span>
                  </td>
                  <td>
                    {!access.isRevoked && (
                      <button 
                        onClick={() => handleRevokeAccess(access.id)}
                        className="btn-small btn-danger"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AccessControl;
